// WEB /adyen-redirect return route (CPS-26589, Milestone B, Task 8, decision 7).
//
// A web card-add whose 3DS2 resolved as a full-page RedirectShopper navigates the
// whole page to the issuer and back here, to <origin>/adyen-redirect, with a
// `redirectResult` on the URL. That navigation destroyed the in-memory flow
// state, so AdyenCardAddContainer stashed the three resume ids in sessionStorage
// before the redirect. On mount we:
//   1. read `redirectResult` off the URL + restore() the stashed ids;
//   2. if BOTH present: ensure a valid B2C token (silent MSAL re-acquire if
//      expired — same AuthManager path the Apollo auth link uses), then fire
//      ADYEN_SUBMIT_PAYMENT_DETAILS with {ids, details:{redirectResult}};
//        - AUTHORISED -> poll readCard(cardId) to Active (reusing
//          useAdyenCardPolling), then navigate to payment-methods;
//        - REFUSED/FAILED/error -> brief message, then navigate;
//      then clear() the stash.
//   3. if EITHER missing: do NOT submit, do NOT error — just navigate to
//      payment-methods. The readCard polling there surfaces the pending card.
//
// This route is web-only: native 3DS2 returns in-app via the custom URL scheme,
// the JS context survives, and no resume/restore is needed (the native helper is
// a no-op). There is no AdyenRedirect.tsx; the route is registered only in
// navigation/index.web.tsx.

import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { Spinner, BaseText } from 'aimo-ui';
import AuthManager from 'components/auth/AuthManager';
import { ADYEN_SUBMIT_PAYMENT_DETAILS } from 'graphql/mutations/adyenSubmitPaymentDetails';
import { useAdyenCardPolling } from 'hooks/useAdyenCardPolling';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenWrapper, StyledWrapper } from 'styles/ContainerStyles';
import type {
  AdyenPaymentResult,
  AdyenStateData,
} from 'components/payment/adyenCardAddSheet.types';
import type { AdyenSubmitDetailsInput } from 'types/generatedSchemaTypes';
import type { AccountScreenNavigationProp } from 'types/navigation';
import {
  clearAdyenRedirectSession,
  restoreAdyenRedirectSession,
} from 'utils/adyenRedirectSession';

import {
  resumeAdyenRedirect,
  type AdyenRedirectOutcome,
} from './adyenRedirectResume';

// What the screen is doing, for the in-tree UX. `resolving` covers both the
// submit round-trip and the post-AUTHORISED activation poll (both show a
// spinner). `refused`/`error` show a brief message before the navigate fires.
type ScreenPhase = 'resolving' | 'refused' | 'error';

// Brief dwell so a terminal message is actually seen before we leave the route.
const TERMINAL_DWELL_MS = 2500;

const AdyenRedirect = (): React.JSX.Element => {
  const { t } = useTranslation();
  const navigation = useNavigation<AccountScreenNavigationProp>();

  const [phase, setPhase] = useState<ScreenPhase>('resolving');
  // The refused outcome's category, used to key the refused copy (mirrors how
  // AdyenCardAddSheet keys it). Falls back to USE_OTHER_CARD when absent.
  const [refusalReasonCategory, setRefusalReasonCategory] = useState<
    string | null | undefined
  >(undefined);
  // Set once submit returns AUTHORISED; flips the activation poller on.
  const [activatingCardId, setActivatingCardId] = useState<string | undefined>(
    undefined
  );
  // Guard so the mount resume runs exactly once even under StrictMode double
  // invoke / re-renders.
  const startedRef = useRef(false);

  const [adyenSubmitPaymentDetails] = useMutation<
    { adyenSubmitPaymentDetails: AdyenPaymentResult },
    { input: AdyenSubmitDetailsInput }
  >(ADYEN_SUBMIT_PAYMENT_DETAILS);

  // Navigate to payment-methods (nested under accountScreen). readCard polling on
  // that screen surfaces the pending/now-active card; we always land there.
  const goToPaymentMethods = (): void => {
    navigation.navigate('accountScreen', { screen: 'myPaymentMethods' });
  };
  // Keep a stable ref so effects can call it without re-running on identity.
  const goToPaymentMethodsRef = useRef(goToPaymentMethods);
  goToPaymentMethodsRef.current = goToPaymentMethods;

  // AUTHORISED path: poll readCard(cardId) to Active, then leave. Mirrors the
  // container's activation gate. timedOut still navigates (the card will appear
  // shortly via the destination's own polling), so both phases route onward.
  const handleSettled = (): void => {
    clearAdyenRedirectSession();
    goToPaymentMethodsRef.current();
  };
  const handleSettledRef = useRef(handleSettled);
  handleSettledRef.current = handleSettled;

  const polling = useAdyenCardPolling({
    cardId: activatingCardId,
    enabled: Boolean(activatingCardId),
    onActivated: () => handleSettledRef.current(),
  });

  // Navigate once the poller reaches a terminal phase (active is handled by
  // onActivated above; timedOut routes onward too).
  useEffect(() => {
    if (polling.phase === 'timedOut') {
      handleSettledRef.current();
    }
  }, [polling.phase]);

  // Mount: run the resume orchestration exactly once.
  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    let cancelled = false;

    const run = async (): Promise<void> => {
      const outcome: AdyenRedirectOutcome = await resumeAdyenRedirect({
        url: globalThis.location?.href ?? '',
        restore: restoreAdyenRedirectSession,
        ensureToken: () => AuthManager.getAccessTokenAsync(),
        submit: async (input) => {
          const response = await adyenSubmitPaymentDetails({
            variables: {
              input: {
                cardId: input.cardId,
                transactionId: input.transactionId,
                checkoutAttemptId: input.checkoutAttemptId,
                // `details` is the BFF JSON scalar; the redirectResult is the
                // only field a redirect resume carries.
                details: input.details as AdyenStateData['details'],
              },
            },
          });
          return response.data?.adyenSubmitPaymentDetails;
        },
      });

      if (cancelled) {
        return;
      }

      switch (outcome.kind) {
        case 'authorised':
          // Hand off to the activation poller; navigation happens on settle.
          setActivatingCardId(outcome.cardId);
          break;
        case 'noSession':
          // Nothing to resume — clear any stale stash and route onward.
          clearAdyenRedirectSession();
          goToPaymentMethodsRef.current();
          break;
        case 'refused':
          setRefusalReasonCategory(outcome.refusalReasonCategory);
          setPhase('refused');
          clearAdyenRedirectSession();
          setTimeout(() => {
            if (!cancelled) {
              goToPaymentMethodsRef.current();
            }
          }, TERMINAL_DWELL_MS);
          break;
        case 'error':
        default:
          setPhase('error');
          clearAdyenRedirectSession();
          setTimeout(() => {
            if (!cancelled) {
              goToPaymentMethodsRef.current();
            }
          }, TERMINAL_DWELL_MS);
          break;
      }
    };

    run().catch(() => {
      // resumeAdyenRedirect already maps every failure to an outcome; this guard
      // only covers an unexpected throw outside it. Route onward rather than
      // hang on the spinner.
      if (!cancelled) {
        clearAdyenRedirectSession();
        goToPaymentMethodsRef.current();
      }
    });

    return () => {
      cancelled = true;
    };
    // startedRef makes the body run exactly once; adyenSubmitPaymentDetails is a
    // stable useMutation tuple member and everything else is read through a ref,
    // so the effect never legitimately re-fires the resume.
  }, [adyenSubmitPaymentDetails]);

  return (
    <ScreenWrapper tabBarHeight={200} testID="adyen-redirect-screen">
      <StyledWrapper center>
        {phase === 'resolving' ? (
          <Spinner testID="adyen-redirect-resolving" />
        ) : (
          <BaseText center testID={`adyen-redirect-${phase}`}>
            {phase === 'refused'
              ? t(
                  `payment.adyen.refused.${refusalReasonCategory ?? 'USE_OTHER_CARD'}`
                )
              : t('payment.adyen.error')}
          </BaseText>
        )}
      </StyledWrapper>
    </ScreenWrapper>
  );
};

export default AdyenRedirect;
